import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Check, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

interface CardReq {
  id: string;
  cardId: string | null;
  vendor: string | null;
  clientName: string | null;
  amount: string | null;
  currency: string | null;
  requesterUserId: string;
  isBeneficiaryAddition: boolean;
  createdAt: string;
}
interface BankReq {
  id: string;
  bankAccountId: string | null;
  beneficiaryName: string | null;
  clientName: string | null;
  amount: string | null;
  requesterUserId: string;
  createdAt: string;
}
interface UserRow {
  id: string;
  name: string;
}
interface CardRow {
  id: string;
  nickname: string | null;
  bank: string | null;
  lastFour: string | null;
}

function money(amount: string | null, currency?: string | null): string {
  if (!amount) return '—';
  const n = Number(amount);
  const sym = !currency || currency === 'INR' ? '₹' : `${currency} `;
  return `${sym}${Number.isFinite(n) ? n.toLocaleString('en-IN') : amount}`;
}
function ago(iso: string): string {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

/**
 * Web approvals queue — PENDING card & bank-transfer requests with
 * Approve / Decline. Hits the same admin-only endpoints the mobile
 * Approvals screen uses, so no routing or approval logic changes.
 */
export function ApprovalsPage() {
  const qc = useQueryClient();
  const cardReqs = useQuery<CardReq[]>({
    queryKey: ['card-requests', 'pending'],
    queryFn: () => api('/api/card-requests/pending'),
    refetchInterval: 15_000,
  });
  const bankReqs = useQuery<BankReq[]>({
    queryKey: ['bank-transfer-requests', 'pending'],
    queryFn: () => api('/api/bank-transfer-requests/pending'),
    refetchInterval: 15_000,
  });
  const users = useQuery<UserRow[]>({ queryKey: ['users'], queryFn: () => api('/api/users') });
  const cards = useQuery<CardRow[]>({ queryKey: ['cards'], queryFn: () => api('/api/cards') });

  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const userName = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users.data ?? []) m.set(u.id, u.name);
    return m;
  }, [users.data]);
  const cardLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of cards.data ?? [])
      m.set(c.id, c.nickname ?? [c.bank, c.lastFour].filter(Boolean).join(' '));
    return m;
  }, [cards.data]);

  const act = async (
    kind: 'card-requests' | 'bank-transfer-requests',
    id: string,
    action: 'approve' | 'decline',
  ) => {
    setBusyId(id);
    setErr(null);
    try {
      await api(`/api/${kind}/${id}/${action}`, { method: 'POST' });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['card-requests', 'pending'] }),
        qc.invalidateQueries({ queryKey: ['bank-transfer-requests', 'pending'] }),
      ]);
    } catch (e) {
      setErr((e as Error).message || `Could not ${action}`);
    } finally {
      setBusyId(null);
    }
  };

  const loading = cardReqs.isLoading || bankReqs.isLoading;
  const cardList = cardReqs.data ?? [];
  const bankList = bankReqs.data ?? [];
  const total = cardList.length + bankList.length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Approvals</h1>
        <p className="text-sm text-muted-foreground">
          Pending OTP requests awaiting an admin's approval — approve or decline here instead of
          on the phone.
        </p>
      </div>

      {err ? (
        <div className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-2.5 text-sm text-rose-800">
          {err}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Pending requests {total > 0 ? `(${total})` : ''}
          </CardTitle>
          <CardDescription>Card &amp; bank-transfer OTP requests.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : total === 0 ? (
            <p className="px-4 py-12 text-center text-sm text-muted-foreground">
              Nothing pending — all caught up.
            </p>
          ) : (
            <ul className="divide-y">
              {cardList.map((r) => (
                <ApprovalRow
                  key={r.id}
                  title={`Card OTP · ${money(r.amount, r.currency)}`}
                  sub={[
                    `raised by ${userName.get(r.requesterUserId) ?? 'teammate'}`,
                    cardLabel.get(r.cardId ?? '') || null,
                    r.vendor,
                    r.clientName,
                    r.isBeneficiaryAddition ? 'beneficiary addition' : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  when={ago(r.createdAt)}
                  busy={busyId === r.id}
                  onApprove={() => void act('card-requests', r.id, 'approve')}
                  onDecline={() => void act('card-requests', r.id, 'decline')}
                />
              ))}
              {bankList.map((r) => (
                <ApprovalRow
                  key={r.id}
                  title={`Bank transfer · ${money(r.amount)}`}
                  sub={[
                    `raised by ${userName.get(r.requesterUserId) ?? 'teammate'}`,
                    r.beneficiaryName ? `to ${r.beneficiaryName}` : null,
                    r.clientName,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  when={ago(r.createdAt)}
                  busy={busyId === r.id}
                  onApprove={() => void act('bank-transfer-requests', r.id, 'approve')}
                  onDecline={() => void act('bank-transfer-requests', r.id, 'decline')}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ApprovalRow({
  title,
  sub,
  when,
  busy,
  onApprove,
  onDecline,
}: {
  title: string;
  sub: string;
  when: string;
  busy: boolean;
  onApprove: () => void;
  onDecline: () => void;
}) {
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {sub ? <p className="mt-0.5 truncate text-xs text-muted-foreground">{sub}</p> : null}
        <p className="mt-0.5 text-[11px] text-muted-foreground">{when}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={onDecline}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
          Decline
        </button>
        <button
          type="button"
          onClick={onApprove}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          <Check className="h-3.5 w-3.5" />
          {busy ? '…' : 'Approve'}
        </button>
      </div>
    </li>
  );
}
